import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import { TiPlus } from 'react-icons/ti';
import { useTranslation } from 'react-i18next';
import { CellProps, Column } from 'react-table';

import Btn from '../../../../components/Buttons/Btn';
import Modal from '../../../../components/Modal';
import { backendErrorNotification, frontendNotification } from '../../../../components/Notification';
import TableLoader from '../../../../components/Loaders/TableLoader';
import TablePaginator from '../../../../components/TablePaginator';
import Tooltip from '../../../../components/Tooltip';
import HttpStatus from '../../../../enums/httpStatus';
import checkPermission from '../../../../utils/checkPermission';
import { PermissionsEnum } from '../../../../enums/permissions.enum';
import {
  productImageByIdAPI,
  productImagesAPI,
  productImagesAppendAPI,
  productImagesByProductAPI,
} from '../../../../services/requests';
import type { Product, ProductImage, ProductImagesResponse } from '../../../../types';
import ProductImageThumbnail from './ProductImageThumbnail';
import ProductImagesViewerModal from './ProductImagesViewerModal';
import * as s from './ProductImageModal.styles';

interface ProductImageModalProps {
  product: Product;
  closeModal: () => void;
  callback: () => void;
}

interface ProductImageTableRow extends Record<string, unknown> {
  image: ProductImage;
  position: number;
  fileName: string;
  fileSize: string;
}

const DEFAULT_MAX_PRODUCT_IMAGES = 6;

const initialImages = (product: Product): ProductImage[] => (
  product.images?.length ? product.images : product.image ? [product.image] : []
);

function formatFileSize(size: number | null): string {
  if (size === null || !Number.isFinite(size) || size < 0) return '-';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

const ProductImageModal = ({ product, closeModal, callback }: ProductImageModalProps) => {
  const { t } = useTranslation();
  const [images, setImages] = useState<ProductImage[]>(() => initialImages(product));
  const [maxImages, setMaxImages] = useState(DEFAULT_MAX_PRODUCT_IMAGES);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingImages, setIsLoadingImages] = useState(true);
  const [showImagesViewer, setShowImagesViewer] = useState(false);
  const [viewerInitialIndex, setViewerInitialIndex] = useState(0);
  const [imagePendingDeletion, setImagePendingDeletion] = useState<ProductImage | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replacementTargetRef = useRef<ProductImage | null>(null);
  const canEdit = checkPermission(PermissionsEnum.PRODUCTS_EDIT);
  const canDelete = checkPermission(PermissionsEnum.PRODUCTS_DELETE);
  const atImageLimit = images.length >= maxImages;

  const fetchImages = useCallback(async (reportError = false) => {
    setIsLoadingImages(true);
    try {
      const response = await productImagesByProductAPI.show(encodeURIComponent(product.CodigoProduto));
      if (response.status !== HttpStatus.OK) throw response;

      const result = response.data?.data as ProductImagesResponse | undefined;
      if (!result || !Array.isArray(result.images)) throw response;

      setImages(result.images);
      setMaxImages(Number(result.max_images_per_product) || DEFAULT_MAX_PRODUCT_IMAGES);
    } catch (error) {
      if (reportError) backendErrorNotification(error as AxiosError<any, any>);
    } finally {
      setIsLoadingImages(false);
    }
  }, [product.CodigoProduto]);

  useEffect(() => {
    void fetchImages(true);
  }, [fetchImages]);

  const openNewImagePicker = () => {
    if (isLoading || isLoadingImages || atImageLimit) return;
    replacementTargetRef.current = null;
    fileInputRef.current?.click();
  };

  const openReplacementPicker = (image: ProductImage) => {
    if (isLoading || isLoadingImages) return;
    replacementTargetRef.current = image;
    fileInputRef.current?.click();
  };

  const uploadImage = async (file: File, replacementTarget: ProductImage | null) => {
    if (!replacementTarget && images.length >= maxImages) return;
    setIsLoading(true);
    try {
      const response = replacementTarget
        ? await productImageByIdAPI.putWithFile(file, 'image', {}, replacementTarget.id)
        : await productImagesAppendAPI.postWithFile(
          file,
          'image',
          { product_code: product.CodigoProduto },
          false,
        );

      if (![HttpStatus.OK, HttpStatus.CREATED].includes(response.status)) throw response;

      frontendNotification({
        message: replacementTarget ? t('productImageUpdateSuccess') : t('productImageUploadSuccess'),
        type: 'success',
      });
      await fetchImages();
      callback();
    } catch (error) {
      backendErrorNotification(error as AxiosError<any, any>);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0] || null;
    const replacementTarget = replacementTargetRef.current;
    replacementTargetRef.current = null;
    event.currentTarget.value = '';

    if (file) void uploadImage(file, replacementTarget);
  };

  const removeImage = async () => {
    if (!imagePendingDeletion || isLoading || isLoadingImages) return;
    setIsLoading(true);
    try {
      const response = imagePendingDeletion.id
        ? await productImageByIdAPI.delete(imagePendingDeletion.id)
        : await productImagesAPI.delete(product.CodigoProduto);
      if (response.status !== HttpStatus.OK) throw response;

      frontendNotification({ message: t('productImageDeleteSuccess'), type: 'success' });
      setImagePendingDeletion(null);
      await fetchImages();
      callback();
    } catch (error) {
      backendErrorNotification(error as AxiosError<any, any>);
    } finally {
      setIsLoading(false);
    }
  };

  const openImageViewer = (index: number) => {
    setViewerInitialIndex(index);
    setShowImagesViewer(true);
  };

  const imageRows = useMemo<ProductImageTableRow[]>(() => images.map((image, index) => ({
    image,
    position: index + 1,
    fileName: image.file_name?.trim() || t('productImageNumber', { current: index + 1 }),
    fileSize: formatFileSize(image.file_size),
  })), [images, t]);

  const columns = useMemo<Column<ProductImageTableRow>[]>(() => [
    {
      id: 'image',
      Header: t('image') as string,
      Cell: ({ row }: CellProps<ProductImageTableRow>) => (
        <ProductImageThumbnail
          image={row.original.image}
          alt={`${product.Descricao} ${row.original.position}`}
          onClick={() => openImageViewer(row.original.position - 1)}
        />
      ),
    },
    {
      Header: t('file') as string,
      accessor: 'fileName',
      Cell: ({ value }: CellProps<ProductImageTableRow>) => (
        <s.FileName title={String(value)}>{String(value)}</s.FileName>
      ),
    },
    {
      Header: t('size') as string,
      accessor: 'fileSize',
    },
    {
      id: 'actions',
      Header: t('actions') as string,
      Cell: ({ row }: CellProps<ProductImageTableRow>) => (
        <s.RowActions>
          {canEdit && (
            <Tooltip title={t('replaceProductImage')}>
              <s.RowActionButton
                type="button"
                aria-label={t('replaceProductImage')}
                onClick={() => openReplacementPicker(row.original.image)}
                disabled={isLoading || isLoadingImages}
              >
                <FiRefreshCw size={16} aria-hidden="true" />
              </s.RowActionButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title={t('removeImage')}>
              <s.RowActionButton
                type="button"
                $danger
                aria-label={t('removeImage')}
                onClick={() => setImagePendingDeletion(row.original.image)}
                disabled={isLoading || isLoadingImages}
              >
                <FiTrash2 size={17} aria-hidden="true" />
              </s.RowActionButton>
            </Tooltip>
          )}
        </s.RowActions>
      ),
    },
  ], [canDelete, canEdit, isLoading, isLoadingImages, product.Descricao, t]);

  return (
    <>
      <Modal
        title={`${t('manageProductImages')}: ${product.Descricao}`}
        showModal
        closeModal={closeModal}
        onlyCloseButton
        isLoading={isLoading}
        style={{ maxWidth: '900px', width: 'calc(100vw - 2rem)' }}
        bodyStyle={{ maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        <s.Toolbar>
          <div>
            <s.ImageCount>{t('productImagesCount', { count: images.length, limit: maxImages })}</s.ImageCount>
            {atImageLimit && (
              <s.LimitHint>{t('productImageLimitHint', { limit: maxImages })}</s.LimitHint>
            )}
          </div>
          <Btn
            Icon={TiPlus}
            text={t('newProductImage')}
            onClick={openNewImagePicker}
            disabled={isLoading || isLoadingImages || atImageLimit}
            style={{ marginLeft: 'auto', marginRight: '0' }}
          />
        </s.Toolbar>

        <TableLoader isLoading={isLoadingImages}>
          {imageRows.length > 0 ? (
            <TablePaginator
              data={imageRows}
              columns={columns}
              size={10}
              showButtons={imageRows.length > 10}
              showMoreRows={imageRows.length > 10}
            />
          ) : (
            <s.EmptyState>{t('noProductImages')}</s.EmptyState>
          )}
        </TableLoader>
      </Modal>

      {showImagesViewer && (
        <ProductImagesViewerModal
          product={product}
          images={images}
          initialIndex={viewerInitialIndex}
          closeModal={() => setShowImagesViewer(false)}
        />
      )}

      {imagePendingDeletion && (
        <Modal
          title={t('removeImage')}
          showModal
          closeModal={() => {
            if (!isLoading) setImagePendingDeletion(null);
          }}
          action={() => { void removeImage(); }}
          actionNameBtn={t('remove')}
          cancelButton={() => {
            if (!isLoading) setImagePendingDeletion(null);
          }}
          isLoading={isLoading}
          style={{ maxWidth: '480px', width: 'calc(100vw - 2rem)' }}
        >
          <s.DeleteConfirmation>
            <p>{t('productImageDeleteConfirmation')}</p>
            <strong>
              {imagePendingDeletion.file_name?.trim()
                || t('productImageNumber', {
                  current: images.findIndex((image) => image.id === imagePendingDeletion.id) + 1,
                })}
            </strong>
          </s.DeleteConfirmation>
        </Modal>
      )}
    </>
  );
};

export default ProductImageModal;
